<script setup lang="ts">
import { computed } from 'vue';
import type { Ref } from 'vue';
import CollapsibleSection from './CollapsibleSection.vue';
import './epp-shared.css';

const props = defineProps<{
  availableAt: Ref<string | null>;
  preparationTime: Ref<number | null>;
}>();

// avalaibleAt is stored/sent as an ISO string but <input type="datetime-local">
// needs "YYYY-MM-DDTHH:mm" with no timezone suffix, so it's converted both ways here.
const availableAtLocal = computed<string>({
  get() {
    const iso = props.availableAt.value;
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  },
  set(value) {
    props.availableAt.value = value ? new Date(value).toISOString() : null;
  },
});

const preparationTimeInput = computed<number | ''>({
  get() {
    return props.preparationTime.value ?? '';
  },
  set(value) {
    props.preparationTime.value = value === '' ? null : Number(value);
  },
});
</script>

<template>
  <CollapsibleSection
    section-key="availability"
    title="Disponibilité"
    subtitle="Quand et comment ce produit devient disponible à la commande"
    icon="pi-clock"
    :default-open="true"
  >
    <div class="epp__fields">
      <div class="epp__field">
        <label class="epp__label" for="epp-available-at"
          >Disponible à partir de</label
        >
        <input
          id="epp-available-at"
          v-model="availableAtLocal"
          class="epp__input"
          type="datetime-local"
        />
        <p class="epp__hint">
          Date et heure à partir desquelles le produit devient achetable par les
          clients. Laissez vide pour le rendre disponible immédiatement.
        </p>
      </div>
      <div class="epp__field">
        <label class="epp__label" for="epp-preparation-time"
          >Temps de préparation (minutes)</label
        >
        <input
          id="epp-preparation-time"
          v-model.number="preparationTimeInput"
          class="epp__input"
          type="number"
          min="0"
          step="1"
          placeholder="ex: 15"
        />
        <p class="epp__hint">
          Durée moyenne nécessaire pour préparer ce produit, utilisée pour
          estimer l'heure de retrait de la commande.
        </p>
      </div>
    </div>
  </CollapsibleSection>
</template>

<style scoped>
.epp__hint {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #999);
  line-height: 1.4;
}
</style>
